<route>
{
  "meta": {
    "permission": [
      "product.add_priceformula",
      "product.change_priceformula",
      "product.add_priceformulaattributes",
      "product.view_priceformulaattributes"
    ]
  }
}
</route>

<template>
  <i-container
    :opts="opts"
    :mode="container"
    @submit="submit"
    @input="$emit('input', $event)"
  >
    <!-- Formulario -->
    <v-card class="mb-4">
      <ValidationObserver v-slot="{ handleSubmit }" ref="form">
        <form @submit.prevent="handleSubmit()">
          <i-toolbar
            :dontSave="container !== 'container'"
            :title="formTitle"
            :loading="load"
          ></i-toolbar>
          <v-card-text>
            <v-row class="m-0">
              <!-- Formulario -->
              <v-col class="formula-pk--container-form" cols="12" md="4" lg="3">
                <h3 class="headline font-weight-bold grey--text mb-4">
                  {{ $t('form') }}
                </h3>

                <!-- Input: Editar nombre -->
                <ValidationProvider
                  vid="name"
                  :name="$tc('name', 1)"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    autocomplete="off"
                    color="secondary"
                    maxlength="64"
                    outlined
                    :error-messages="errors[0]"
                    :label="$tc('name', 1)"
                    v-model="formula.name"
                  />
                </ValidationProvider>

                <!-- Atributos -->
                <ValidationProvider
                  v-if="checkPermissions.view"
                  vid="attributes"
                  :name="$tc('attributes', 1)"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-autocomplete
                    autocomplete="off"
                    chips
                    class="secondary--text attibutes"
                    deletable-chips
                    key="attributes-input"
                    multiple
                    outlined
                    :error-messages="errors[0]"
                    :items="uoms"
                    item-text="name"
                    item-value="pk"
                    :label="$t('attributes')"
                    :loading="isLoading"
                    :menu-props="{ closeOnContentClick: true }"
                    :prepend-inner-icon="checkPermissions.add && 'fa-plus'"
                    :search-input.sync="searchAttributes"
                    @click:prepend-inner="createAttr()"
                    v-model="formula.attributes"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title
                          v-html="$tc(item.name.toLowerCase(), 1)"
                        ></v-list-item-title>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="data">
                      <v-chip
                        class="white--text"
                        close
                        color="secondary"
                        small
                        :input-value="data.selected"
                        @click:close="removeAttr(data.item)"
                        @click="data.select"
                        v-bind="data.attrs"
                      >
                        {{ $tc(data.item.name.toLowerCase(), 1) }}
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </ValidationProvider>

                <!-- Modelo -->
                <v-switch
                  v-if="isAdmin"
                  class="v-switch px-3"
                  color="primary"
                  name="formula_model"
                  hide-details
                  :label="$tc('formula_model')"
                  v-model="formula.model"
                ></v-switch>
              </v-col>

              <!-- Laboratorio de fórmulas -->
              <v-col cols="12" md="8" lg="9">
                <!-- Título -->
                <h3 class="headline font-weight-bold grey--text">
                  {{ `${$t('section', { value: $t('formula') })}` }}
                </h3>

                <!-- Sandbox de la fórmula -->
                <div class="formula-pk--formula-container">
                  <div class="formula-pk--formula-name">{{ formula.name }}</div>
                  <p
                    class="font-weight-bold text-center mb-0"
                    color="primary"
                    text-color="white"
                  >
                    =
                  </p>
                  <draggable
                    v-if="show"
                    class="formula-pk--chip-container mb-0"
                    :class="{
                      'error lighten-1': show_formula == 'formulaNoValid',
                      'warning lighten-1': show_formula == 'formulaDivisionZero'
                    }"
                    v-model="formula.formula"
                  >
                    <transition-group>
                      <v-chip
                        class="formula elementList formula-pk--chip-formula"
                        close
                        text-color="white"
                        color="secondary"
                        :key="'chipsFormula-' + attr_selected + index"
                        @click:close="removeChip(index)"
                        v-for="(attr_selected, index) in formula.formula"
                      >
                        <span class="font-weight-bold">
                          {{
                            attr_selected.indexOf('var_') != -1
                              ? show_attrs.find(
                                  (o) => attr_selected === `var_${o.pk}`
                                ) == undefined
                                ? ''
                                : $tc(
                                    show_attrs
                                      .find(
                                        (o) => attr_selected === `var_${o.pk}`
                                      )
                                      .name.toLowerCase(),
                                    1
                                  )
                              : attr_selected
                          }}
                        </span>
                      </v-chip>
                    </transition-group>
                  </draggable>
                </div>

                <!-- Alerta en caso que la fórmula sea inválida -->
                <div class="formula-pk--formula-container">
                  <span></span>
                  <span></span>
                  <p
                    v-if="
                      show_formula == 'formulaNoValid' ||
                      show_formula == 'formulaDivisionZero'
                    "
                    class="font-weight-bold mt-4"
                    :class="`${
                      show_formula == 'formulaDivisionZero'
                        ? 'warning--text'
                        : 'error--text'
                    }`"
                  >
                    {{ $t(show_formula) }}
                  </p>
                </div>

                <!-- Atributos -->
                <div class="mb-5">
                  <p class="font-weight-bold mb-1">
                    {{ $t('attributes') }}
                  </p>
                  <v-chip
                    v-for="attr_selected in show_attrs"
                    :key="'chips-' + attr_selected.pk"
                    class="mr-1"
                    color="secondary"
                    text-color="white"
                    @click="addAttr(attr_selected)"
                  >
                    {{ $tc(attr_selected.name.toLowerCase(), 1) }}
                  </v-chip>
                </div>

                <!-- Teclado tipo calculadora -->
                <div class="mb-5">
                  <p class="font-weight-bold mb-1">
                    {{ $t('operators') }}
                  </p>
                  <div
                    class="d-flex justify-content-start g-1 flex-wrap"
                    style="width: 160px"
                  >
                    <v-chip
                      v-for="operator in operators"
                      class="formula-pk--operator-chip"
                      :class="operator.value == 0 ? 'formula-pk--zero' : ''"
                      :color="operator.color"
                      :text-color="operator.text"
                      :key="'chips-' + operator.value"
                      @click="addAttr(operator.value)"
                    >
                      {{ operator.value }}
                    </v-chip>
                  </div>
                </div>

                <!-- Cantidad específica -->
                <div>
                  <p class="font-weight-bold mb-n2">
                    {{ $t('add_quantity') }}
                  </p>
                  <ValidationProvider
                    vid="number"
                    :name="$tc('number', 1)"
                    rules="double"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      class="mt-4"
                      type="number"
                      append-icon="mdi-send"
                      autocomplete="off"
                      color="secondary"
                      outlined
                      :error-messages="errors[0]"
                      :label="$tc('number', 1)"
                      @click:append="addAttr(number, 1)"
                      @keydown.enter.prevent="addAttr(number, 1)"
                      v-model="number"
                    />
                  </ValidationProvider>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </form>
      </ValidationObserver>
    </v-card>
    <!-- Simulación de la fórmula -->
    <v-alert
      class="font-weight-black title"
      outlined
      :type="
        show_formula == 'formulaNoValid'
          ? 'error'
          : show_formula == 'formulaDivisionZero'
          ? 'warning'
          : 'info'
      "
      :value="true"
    >
      <p class="formulapk-alert__title grey--text">{{ $t('test_formula') }}:</p>
      <i-formula
        ref="refformula"
        :measure="testMeasureUnit"
        :formula="formula.pk"
        @change="total = $event"
        v-model="show_formula"
      />
      <v-row class="my-0">
        <v-col
          cols="12"
          md="5"
          order="1"
          order-md="2"
          class="pt-0 grey--text darker-2"
        >
          {{ localTranslate(show_formula_example) }}
        </v-col>
        <v-col class="pt-0" cols="12" md="7" order="2" order-md="1">
          <p class="mb-0">
            {{ $t('formula') }}:
            {{
              show_formula == 'formulaNoValid' ||
              show_formula == 'formulaDivisionZero'
                ? $t(show_formula)
                : show_formula
            }}
          </p>
          <p>Total: {{ isNaN(total) ? 0 : total }}</p>
        </v-col>
      </v-row>
    </v-alert>

    <!-- Modal de crear fórmula -->
    <v-dialog v-model="dialogName" max-width="390" persistent>
      <v-card>
        <v-toolbar dark color="primary" class="mb-5">
          <v-btn text dark @click="$router.back()">
            <v-icon>fa-times</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-toolbar-title class="font-weight-black">
            {{ $t('add', { value: $tc('title') }) }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            text
            dark
            @click="addName"
            :loading="isLoading"
            :disabled="!btnSaveName"
          >
            <template v-slot:loader>
              <v-progress-circular color="white" indeterminate width="3">
                <v-icon size="20" v-text="'$vuetify.icons.loading'"></v-icon>
              </v-progress-circular>
            </template>
            <v-icon>fa-save</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="mt-5">
          <v-row>
            <v-col cols="12">
              <v-text-field
                :label="$t('name')"
                outlined
                v-model="formula.name"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal de crear atributos de fórmula -->
    <dial-attributes
      v-if="checkPermissions.view"
      :to-edit="toEdit"
      @done="addAttribute"
      v-model="dialog"
    />
  </i-container>
</template>
<script>
import { mapGetters } from 'vuex'
import DialAttributes from '@/components/attributes/modal.vue'
import { translateFormula } from '@/plugins/formula'
import draggable from 'vuedraggable'
export default {
  props: {
    container: {
      type: String,
      default: 'container'
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  components: {
    DialAttributes,
    draggable
  },
  data() {
    return {
      formula: {
        pk: '',
        name: '',
        attributes: [],
        formula: [],
        model: false,
        company_id: ''
      },
      show_attrs: [],
      show_formula: '',
      show_formula_example: '',
      number: null,
      operators: [
        { value: '^', color: 'primary', text: 'white' },
        { value: '(', color: 'primary', text: 'white' },
        { value: ')', color: 'primary', text: 'white' },
        { value: '/', color: 'secondary', text: 'white' },
        { value: '7', color: 'muted', text: 'auxtext' },
        { value: '8', color: 'muted', text: 'auxtext' },
        { value: '9', color: 'muted', text: 'auxtext' },
        { value: '*', color: 'secondary', text: 'white' },
        { value: '4', color: 'muted', text: 'auxtext' },
        { value: '5', color: 'muted', text: 'auxtext' },
        { value: '6', color: 'muted', text: 'auxtext' },
        { value: '-', color: 'secondary', text: 'white' },
        { value: '1', color: 'muted', text: 'auxtext' },
        { value: '2', color: 'muted', text: 'auxtext' },
        { value: '3', color: 'muted', text: 'auxtext' },
        { value: '+', color: 'secondary', text: 'white' },
        { value: '0', color: 'muted', text: 'auxtext' },
        { value: '.', color: 'primary', text: 'white' }
      ],
      total: 0,
      searchAttributes: '',
      uoms: [],
      dialog: false,
      isLoading: false,
      isLoading2: false,
      loading: false,
      load: false,
      valid: false,
      show: true,
      toEdit: null,
      reload: false,
      dialogName: false,
      testMeasureUnit: 'in'
    }
  },
  computed: {
    opts() {
      switch (this.container) {
        case 'modal':
          return {
            title: this.$t(this.edit ? 'editing' : 'add', {
              value: ''
            }),
            value: this.value,
            load: this.loading,
            maxWidth: '80%'
          }
        default:
          return { fluid: true }
      }
    },
    ...mapGetters({
      i_formula_formula: 'formula/show_formula',
      i_formula_attrs: 'formula/show_attrs',
      getPermissions: 'session/getPermissions',
      company: 'company/getCompanyData',
      isAdmin: 'session/isAdmin'
    }),
    /**
     * Determina si el usuario tiene los permisos necesarios según la indicación
     *
     * @return {boolean}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    checkPermissions() {
      return {
        add: this.getPermissions([`product.add_priceformulaattributes`]),
        view: this.getPermissions([`product.view_priceformulaattributes`])
      }
    },
    btnSaveName() {
      if (this.formula.name != '') {
        return true
      }
      return false
    },
    /**
     * Computado que determina el nombre del formulario en función del tipo
     *
     * @returns {string} - Título.
     *
     * Angelo Osorio <danielking.321 at gmail.com>
     */
    formTitle() {
      const titlesMap = {
        formula: this.$tc('formula', 1),
        edit_formula: `${this.$t('edit')} ${this.$tc('formula', 1)}`,
        add_formula: this.$t('add', { value: this.$tc('formula', 1) })
      }
      if (this.container === 'modal') return titlesMap.formula
      if (this.formula.pk) return titlesMap.edit_formula
      return titlesMap.add_formula
    }
  },
  methods: {
    addName() {
      if (this.formula.name != '') {
        this.dialogName = false
      }
    },
    createAttr() {
      this.toEdit = null
      this.dialog = true
      this.reload = true
      this.getUom()
    },
    removeAttr(item) {
      const index = this.formula.attributes.findIndex((m) => m == item.pk)
      if (index >= 0) {
        this.formula.attributes.splice(index, 1)
        this.formula.formula = this.formula.formula.filter(
          (m) => m != `var_${item.pk}`
        )
      }
    },
    removeChip(index) {
      this.formula.formula.splice(index, 1)
    },
    addAttr(val, num) {
      if (typeof val === 'object') {
        this.formula.formula.push(`var_${val.pk}`)
      } else {
        if (num == undefined) {
          this.formula.formula.push(val)
        } else {
          this.number = ''
          this.formula.formula.push(`${Number(val)}`)
        }
      }
    },
    /**
     * Método para agregar un nuevo atributo a la lista, si se proporciona un
     * evento.
     *
     * @param {Object} evt - Evento que puede contener un nuevo atributo
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    addAttribute(evt) {
      this.formula.attributes.push(evt.pk)
      this.getUom()
    },
    async getUom() {
      let response = await this.$api.formula.attributes.list({})
      this.uoms = await this.i_formula_attrs(this.formula.attributes)
      this.uoms = Array.from(new Set(this.uoms.concat(response.data)))
      this.isLoading = false
    },
    async submit() {
      try {
        this.load = true
        this.formula.company = this.company.pk
        if (this.formula.model) {
          this.formula.company = ''
        }
        const formulaContainer =
          this.formula.pk === '' || this.formula.pk === undefined
            ? await this.$api.formula.create({
                form: this.formula
              })
            : await this.$api.formula.edit({
                pk: this.formula.pk,
                form: this.formula
              })
        this.$emit('input', false)
        this.$toast.success(
          `${this.$tc('formula', 1)} ${this.$tc(
            this.formula.pk !== '' ? 'edited' : 'created',
            2
          )}`
        )
        if (this.container === 'container') {
          this.$router.push({ name: 'formula' })
        } else {
          this.$emit('updated', formulaContainer.data)
        }
      } finally {
        this.load = false
      }
    },
    getFormulas() {
      this.loading = true
      let URLDecoded = this.codification({
        mode: 'decode',
        code: this.$route.params.pk
      })
      if (URLDecoded == 'create') {
        if (this.$store.getters['utils/getIsloading']) {
          this.$store.dispatch('utils/actionLoading')
        }
        this.formula = {
          pk: '',
          name: '',
          attributes: [],
          formula: [],
          model: false,
          company_id: ''
        }
        this.dialogName = true
      } else {
        this.$api.formula
          .show({ pk: URLDecoded })
          .then((res) => {
            this.formula = res.data
          })
          .catch(() => this.$router.post({ name: 'formula' }))
      }
      this.getUom()
    },
    localTranslate(formula) {
      return translateFormula(formula, this.$tc.bind(this))
    }
  },
  watch: {
    searchAttributes(val) {
      if (val != undefined && val.length >= 2) {
        this.isLoading = true
        this.getUom(val)
      } else {
        this.getUom()
      }
    },
    'formula.attributes': {
      async handler(val) {
        this.show_attrs = await this.i_formula_attrs(val)
      }
    },
    'formula.formula': {
      async handler(val) {
        this.show_formula_example = await this.i_formula_formula({
          formula: val,
          attributes: this.formula.attributes
        })
        this.show_formula_example = this.show_formula_example['formula']
      }
    },
    show_formula(val) {
      if (val == 'formulaNoValid' || val == 'formulaDivisionZero') {
        this.$toast.error(`${this.$tc(val, 1)}`)
      }
    }
  },
  mounted() {
    if (this.container === 'container') {
      this.getFormulas()
    }
  }
}
</script>
<style lang="sass">
.formulapk-alert__title
  margin-bottom: 12px
  border-bottom: 1px solid var(--v-info-base)
  padding-bottom: 8px
  line-height: 1.5rem
.formula-pk
  &--formula-container
    display: grid
    grid-template-columns: 1fr 14px 6fr
    align-items: center
    gap: 5px
  &--formula-name
    display: block
    padding: .5rem
    color: #FFFFFF
    font-weight: 700
    border-radius: 18px
    text-align: center
    background-color: var(--v-primary-base)
  &--chip-container
    padding: 20px
    background-color: rgb(0 0 0 / 14%)
    border-radius: 18px
    width: 100%
    & > span
      display: flex
      gap: 5px
      flex-wrap: wrap
  &--chip-formula
    font-size: 18px
    line-height: 18px
    &:hover
      cursor: grab
    &.elementList
      span.v-chip__content
        padding-left: 5px
        padding-right: 5px
  &--operator-chip
    min-height: 34px
    min-width: 34px
    align-items: center
    justify-content: center
    &.v-chip.v-size--default
      font-size: 18px
      line-height: 18px
  &--zero
    width: 112px

.theme--dark
  .formula-pk--chip-container
    background-color: rgb(255 255 255 / 14%)

.attibutes.v-select.v-select--chips:not(.v-text-field--single-line)
  .v-select__selections
    min-height: 42px

.formula-pk--container-form
  border: 1px solid rgb(0 0 0 / 38%)
  border-radius: 10px
  .v-switch
    border: 1px solid rgb(0 0 0 / 38%)
    border-radius: 10px
    margin-top: 0
    padding-top: 0
    margin-bottom: 1rem
.theme--dark
  .formula-pk--container-form
    background-color: rgb(255 255 255 / 14%)
  .v-switch
    border-color: rgb(255 255 255 / 20%)
</style>
